import axios, { AxiosInstance, AxiosResponse } from 'axios'
import toast from 'react-hot-toast'
import { useAuthStore } from '@store/authStore'

// API Configuration - Use relative URLs to go through nginx proxy
const API_BASE_URL = ''

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // Increased timeout to 30 seconds
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Prevent multiple 401 redirects when several requests fail at once
let is401Redirecting = false

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response
  },
  (error) => {
    if (error.response?.status === 401) {
      if (!is401Redirecting) {
        is401Redirecting = true
        // Clear auth state completely (store + localStorage) to prevent redirect loop
        useAuthStore.getState().logout()
        window.location.replace('/')
      }
    } else if (error.response?.status >= 500) {
      toast.error('Server error. Please try again later.')
    } else if (error.response?.data?.error) {
      const err = error.response.data.error
      const message = typeof err === 'string' ? err : (err.message || 'An error occurred')
      toast.error(message)
    } else if (error.message) {
      toast.error(error.message)
    } else {
      toast.error('An unexpected error occurred')
    }
    return Promise.reject(error)
  }
)

// Type definitions
export interface PaginationParams {
  page?: number
  limit?: number
  search?: string
  sort?: string
  order?: 'asc' | 'desc'
}

export interface ApplicationFilters extends PaginationParams {
  status?: 'DRAFT' | 'SUBMITTED' | 'UNDER_REVIEW' | 'APPROVED' | 'REJECTED' | 'HIRED' | 'WITHDRAWN'
  roleAppliedFor?: 'DRIVER' | 'CONDUCTOR'
}

export interface PaginatedResponse<T> {
  data: T[]
  pagination: {
    page: number
    limit: number
    total: number
    pages: number
  }
}

export interface AdminBookingMetadata {
  bookingSource?: 'admin' | 'passenger'
  createdByAdmin?: boolean
  createdByAdminId?: string | null
  paymentResponsibility?: 'passenger' | 'admin' | 'account'
  paymentSourceAccount?: string | null
  settlementStatus?: 'pending' | 'cleared'
  settlementClearedAt?: string | null
  settlementClearedById?: string | null
  invoiceGroup?: string | null
  internalNote?: string | null
}

export interface Route {
  id: number
  name: string
  routeCode: string
  startPoint: string
  endPoint: string
  roadNames?: string[]
  distanceKm?: number
  estimatedDurationMinutes?: number
  baseFare?: number
  active: boolean
  createdAt: string
  updatedAt: string
}

export interface CreateRouteData {
  name: string
  routeCode: string
  startPoint: string
  endPoint: string
  roadNames?: string[]
  distanceKm?: number
  estimatedDurationMinutes?: number
  baseFare?: number
  stops?: RouteStopData[]
}

export interface RouteStopData {
  name: string
  latitude: number
  longitude: number
  address?: string
  distanceFromStart?: number
  fareFromStart?: number
  order: number
}

export interface RouteStop {
  id: number
  routeId: number
  stopId: number
  stopOrder: number
  stop: {
    id: number
    name: string
    stopCode: string
    latitude: number
    longitude: number
  }
}

export interface RouteFare {
  id: number
  routeId: number
  fromStopId: number
  toStopId: number
  fare: number
}

export interface SegmentFareResponse {
  route_id: string
  from_stop_id: string
  to_stop_id: string
  from_stop_name: string
  to_stop_name: string
  fare: number
  amount: number
  currency: string
}

export interface RouteStatistics {
  totalTrips: number
  totalPassengers: number
  totalRevenue: number
  averageOccupancy: number
}

function toIsoDateTime(value: string): string {
  const parsed = new Date(value)

  if (Number.isNaN(parsed.getTime())) {
    return value
  }

  return parsed.toISOString()
}

const defaultBookingCancellationSettingsResponse = () => ({
  success: true,
  data: {
    enabled: true,
    allowedHours: 2,
    feePercentage: 10,
    requireReason: true,
    refundToWallet: true,
  },
})

// API Service Class
export class CMSService {
  // Health Check
  async healthCheck() {
    const response = await apiClient.get('/health')
    return response.data
  }

  // Routes API - Updated to use CMS endpoints
  async getRoutes(params?: PaginationParams): Promise<PaginatedResponse<Route>> {
    const response = await apiClient.get('/api/routes', { params })
    return response.data
  }

  async getRouteById(id: number | string): Promise<{ data: Route }> {
    const response = await apiClient.get(`/api/routes/${id}`)
    return response.data
  }

  async searchRoutes(params: {
    query?: string
    startPoint?: string
    endPoint?: string
    maxDistance?: number
    minDistance?: number
    status?: string
  }): Promise<{ data: Route[] }> {
    const response = await apiClient.get('/api/routes/search', { params })
    return response.data
  }

  async createRoute(routeData: CreateRouteData): Promise<{ data: Route; message: string }> {
    // Transform the data to match backend expectations
    const transformedData = {
      routeNumber: routeData.routeCode,
      routeName: routeData.name,
      startPoint: routeData.startPoint,
      endPoint: routeData.endPoint,
      distanceKm: routeData.distanceKm,
      estimatedTimeMinutes: routeData.estimatedDurationMinutes,
      baseFare: routeData.baseFare,
      roadNames: routeData.roadNames,
      status: 'active',
      stops: routeData.stops?.map(stop => ({
        name: stop.name,
        latitude: stop.latitude,
        longitude: stop.longitude,
        address: stop.address,
        distanceFromStart: stop.distanceFromStart,
        fareFromStart: stop.fareFromStart
      })) || []
    }

    const response = await apiClient.post('/api/routes', transformedData)
    return response.data
  }

  async updateRoute(id: number, routeData: Partial<CreateRouteData>): Promise<{ data: Route; message: string }> {
    const response = await apiClient.put(`/api/routes/${id}`, routeData)
    return response.data
  }

  async updateRouteStatus(routeId: string, active: boolean) {
    const response = await apiClient.patch(`/api/routes/${routeId}`, { active })
    return response.data
  }

  async deleteRoute(id: number): Promise<{ message: string }> {
    const response = await apiClient.delete(`/api/routes/${id}`)
    return response.data
  }

  async recalculateAllFares() {
    const response = await apiClient.post('/api/routes/recalculate-all-fares')
    return response.data
  }

  // Route Stops Management
  async addRouteStop(routeId: number, stopData: {
    stopId: string
    stopOrder: number
    distanceFromStart?: number
    fareFromStart?: number
  }) {
    const response = await apiClient.post(`/api/routes/${routeId}/stops`, stopData)
    return response.data
  }

  async updateRouteStop(routeId: number, stopId: string, stopData: {
    stopOrder?: number
    distanceFromStart?: number
    fareFromStart?: number
  }) {
    const response = await apiClient.put(`/api/routes/${routeId}/stops/${stopId}`, stopData)
    return response.data
  }

  async removeRouteStop(routeId: number, stopId: string) {
    const response = await apiClient.delete(`/api/routes/${routeId}/stops/${stopId}`)
    return response.data
  }

  // Route Fares Management
  async addRouteFare(routeId: number, fareData: {
    fromStopId: string
    toStopId: string
    fare: number
  }) {
    const response = await apiClient.post(`/api/routes/${routeId}/fares`, fareData)
    return response.data
  }

  async updateRouteFare(routeId: number, fareId: number, fare: number) {
    const response = await apiClient.put(`/api/routes/${routeId}/fares/${fareId}`, { fare })
    return response.data
  }

  async removeRouteFare(routeId: number, fareId: number) {
    const response = await apiClient.delete(`/api/routes/${routeId}/fares/${fareId}`)
    return response.data
  }

  // Bulk Route Operations
  async bulkCreateRoutes(routes: Array<{
    routeCode: string
    name: string
    startPoint: string
    endPoint: string
    distanceKm?: number
    estimatedDurationMinutes?: number
    baseFare?: number
  }>) {
    const response = await apiClient.post('/api/routes/bulk', { routes })
    return response.data
  }

  async bulkUpdateRoutes(updates: Array<{
    id: number
    name?: string
    startPoint?: string
    endPoint?: string
    distanceKm?: number
    estimatedDurationMinutes?: number
    baseFare?: number
    active?: boolean
  }>) {
    const response = await apiClient.put('/api/routes/bulk', { updates })
    return response.data
  }

  async bulkDeleteRoutes(routeIds: number[]) {
    const response = await apiClient.delete('/api/routes/bulk', { data: { routeIds } })
    return response.data
  }

  // Route Export/Import
  async exportRoutesCSV() {
    const response = await apiClient.get('/api/routes/export/csv', { responseType: 'blob' })
    return response.data
  }

  async exportRoutesExcel() {
    const response = await apiClient.get('/api/routes/export/excel', { responseType: 'blob' })
    return response.data
  }

  async importRoutesCSV(file: File) {
    const formData = new FormData()
    formData.append('file', file)
    const response = await apiClient.post('/api/routes/import/csv', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return response.data
  }

  async getRouteStops(id: number | string): Promise<{ data: RouteStop[] }> {
    try {
      const response = await apiClient.get(`/api/routes/${id}/stops`)
      return response.data
    } catch (error: any) {
      if (error?.response?.status === 404) {
        return { data: [] }
      }
      throw error
    }
  }

  async getRouteFares(id: number | string): Promise<{ data: RouteFare[] }> {
    try {
      const response = await apiClient.get(`/api/routes/${id}/fares`)
      return response.data
    } catch (error: any) {
      if (error?.response?.status === 404) {
        return { data: [] }
      }
      throw error
    }
  }

  async getRouteFareBetweenStops(
    id: number | string,
    params: { startStopId: string; endStopId: string }
  ): Promise<{ status: string; data: SegmentFareResponse | null }> {
    try {
      const response = await apiClient.get(`/api/routes/${id}/fare`, {
        params: {
          start_stop_id: params.startStopId,
          end_stop_id: params.endStopId,
        },
      })
      return response.data
    } catch (error: any) {
      if (error?.response?.status === 404) {
        return { status: 'success', data: null }
      }
      throw error
    }
  }

  async getRouteStatistics(id: number): Promise<{ data: RouteStatistics }> {
    const response = await apiClient.get(`/api/routes/${id}/statistics`)
    return response.data
  }

  // Vehicles/Daladalas API - Connected to CMS
  async getVehicles(params?: PaginationParams) {
    const response = await apiClient.get('/api/cms/vehicles', { params })
    return response.data
  }

  async createVehicle(vehicleData: {
    registrationNumber: string
    ownerId?: string
    driverId?: string
    conductorId?: string
    make?: string
    model?: string
    year?: number
    totalSeats: number
    currentRouteId?: string
  }) {
    const response = await apiClient.post('/api/cms/vehicles', vehicleData)
    return response.data
  }

  async getVehicleById(id: string) {
    const response = await apiClient.get(`/api/cms/vehicles/${id}`)
    return response.data
  }

  async updateVehicle(id: string, vehicleData: {
    registrationNumber?: string
    make?: string
    model?: string
    year?: number
    totalSeats?: number
    status?: 'active' | 'inactive' | 'maintenance'
    driverId?: string | null
    conductorId?: string | null
    ownerId?: string | null
    currentRouteId?: string | null
    seatLayout?: {
      layoutType: string
      rows?: number
      lastRowFull?: boolean
      reservedSeats?: number[]
      middleSeatRows?: number[]
    }
  }) {
    const response = await apiClient.patch(`/api/cms/vehicles/${id}`, vehicleData)
    return response.data
  }

  async deleteVehicle(id: string) {
    const response = await apiClient.delete(`/api/cms/vehicles/${id}`)
    return response.data
  }

  async updateVehicleStatus(id: string, status: 'active' | 'inactive' | 'maintenance') {
    const response = await apiClient.patch(`/api/cms/vehicles/${id}/status`, { status })
    return response.data
  }

  // Driver Assignment
  async assignDriver(daladalaId: string, driverId: string) {
    const response = await apiClient.patch(`/api/cms/vehicles/${daladalaId}/assign-driver`, { driverId })
    return response.data
  }

  async removeDriver(daladalaId: string) {
    const response = await apiClient.patch(`/api/cms/vehicles/${daladalaId}/remove-driver`)
    return response.data
  }

  async getAvailableDrivers() {
    const response = await apiClient.get('/api/cms/drivers/available')
    return response.data
  }

  // Route Assignment
  async assignRoute(daladalaId: string, routeId: string) {
    const response = await apiClient.patch(`/api/cms/vehicles/${daladalaId}/assign-route`, { routeId })
    return response.data
  }

  async getAllRoutesForCMS() {
    const response = await apiClient.get('/api/routes')
    return response.data
  }

  // Timetable Management
  async createTimetable(daladalaId: string, data: {
    routeId: string
    dayOfWeek: number
    departureTime: string
    arrivalTime: string
  }) {
    const response = await apiClient.post(`/api/cms/vehicles/${daladalaId}/timetables`, data)
    return response.data
  }

  async deleteTimetable(timetableId: string) {
    const response = await apiClient.delete(`/api/cms/timetables/${timetableId}`)
    return response.data
  }

  // Dashboard Stats - Updated to use CMS endpoints
  async getDashboardStats() {
    const response = await apiClient.get('/api/cms/dashboard/statistics')
    return response.data
  }

  async getRevenueData(params?: { timeRange?: string; route?: string }) {
    const response = await apiClient.get('/api/cms/dashboard/revenue', {
      params: { period: params?.timeRange || '6m' }
    })
    return response.data
  }

  async getBookingsData(params?: { timeRange?: string; period?: string }) {
    const response = await apiClient.get('/api/cms/dashboard/bookings', {
      params: { period: params?.timeRange || '7d' }
    })
    return response.data
  }

  async getRoutePerformance(params?: { timeRange?: string; route?: string }) {
    const response = await apiClient.get('/api/cms/dashboard/route-performance', {
      params: {
        period: params?.timeRange || '30d',
        route: params?.route
      }
    })
    return response.data
  }

  async getPaymentBreakdown(params?: { timeRange?: string; route?: string }) {
    const response = await apiClient.get('/api/cms/dashboard/payment-breakdown', {
      params: { period: params?.timeRange || '30d' }
    })
    return response.data
  }

  // Driver Management
  async getDrivers(params?: PaginationParams) {
    const response = await apiClient.get('/api/cms/drivers', { params })
    return response.data
  }

  /** Companies for pickers. Shaped like the old owner list so existing forms keep working. Staff only. */
  async getOperators() {
    const response = await apiClient.get('/api/cms/operators', { params: { limit: 100 } })
    return { data: (response.data?.data ?? []).map((o: any) => ({ id: o.id, firstName: o.name, lastName: '', isHouse: o.isHouse })) }
  }

  async createDriver(driverData: {
    firstName: string
    lastName: string
    email: string
    phoneNumber: string
    password: string
    licenseNumber?: string
    licenseExpiry?: string
  }) {
    const response = await apiClient.post('/api/cms/drivers', driverData)
    return response.data
  }

  async updateDriverStatus(driverId: string, status: 'active' | 'inactive' | 'suspended') {
    const response = await apiClient.patch(`/api/cms/drivers/${driverId}/status`, { status })
    return response.data
  }

  async updateDriver(driverId: string, driverData: {
    firstName?: string
    lastName?: string
    email?: string
    phoneNumber?: string
    licenseNumber?: string
    licenseExpiry?: string
  }) {
    const response = await apiClient.put(`/api/cms/drivers/${driverId}`, driverData)
    return response.data
  }

  async approveDriver(driverId: string) {
    const response = await apiClient.patch(`/api/cms/drivers/${driverId}/approve`)
    return response.data
  }

  async suspendDriver(driverId: string, reason?: string) {
    const response = await apiClient.patch(`/api/cms/drivers/${driverId}/suspend`, { reason })
    return response.data
  }

  async deleteDriver(driverId: string) {
    const response = await apiClient.delete(`/api/cms/drivers/${driverId}`)
    return response.data
  }

  async resetDriverPassword(driverId: string, password: string, confirmPassword: string) {
    const response = await apiClient.patch(`/api/cms/drivers/${driverId}/reset-password`, {
      password,
      confirmPassword
    })
    return response.data
  }

  async getDriverTrips(driverId: string, params?: {
    status?: string
    date?: string
    page?: number
    limit?: number
  }) {
    const response = await apiClient.get(`/api/cms/drivers/${driverId}/trips`, { params })
    return response.data
  }

  async uploadDriverDocument(driverId: string, documentData: {
    documentType: string
    documentNumber?: string
    expiryDate?: string
    fileUrl: string
  }) {
    const response = await apiClient.post(`/api/cms/drivers/${driverId}/documents`, documentData)
    return response.data
  }

  async deleteDriverDocument(driverId: string, documentId: string) {
    const response = await apiClient.delete(`/api/cms/drivers/${driverId}/documents/${documentId}`)
    return response.data
  }

  async updateDriverProfile(driverId: string, profileData: {
    firstName?: string
    lastName?: string
    email?: string
    phoneNumber?: string
  }) {
    const response = await apiClient.put(`/api/cms/drivers/${driverId}/profile`, profileData)
    return response.data
  }

  async bulkCreateDrivers(drivers: Array<{
    firstName: string
    lastName: string
    email: string
    phoneNumber: string
    password: string
  }>) {
    const response = await apiClient.post('/api/cms/drivers/bulk', { drivers })
    return response.data
  }

  async bulkUpdateDrivers(updates: Array<{
    id: string
    firstName?: string
    lastName?: string
    email?: string
    phoneNumber?: string
    status?: string
  }>) {
    const response = await apiClient.put('/api/cms/drivers/bulk', { updates })
    return response.data
  }

  async bulkDeleteDrivers(driverIds: string[]) {
    const response = await apiClient.delete('/api/cms/drivers/bulk', { data: { driverIds } })
    return response.data
  }

  // Bookings Management
  async getBookings(params?: PaginationParams & {
    status?: string
    date?: string
    search?: string
  }) {
    const response = await apiClient.get('/api/cms/bookings', { params })
    return response.data
  }

  async getBookingById(bookingId: string) {
    const response = await apiClient.get(`/api/cms/bookings/${bookingId}`)
    return response.data
  }

  async updateBookingStatus(bookingId: string, status: string) {
    const response = await apiClient.patch(`/api/cms/bookings/${bookingId}/status`, { status })
    return response.data
  }

  async createBooking(bookingData: {
    userId: string
    tripId: string
    seatNumbers: number[]
    boardingStopId?: string
    alightingStopId?: string
    fareAmount?: number
    paymentStatus?: 'pending' | 'paid' | 'failed' | 'refunded'
    passengerName?: string
    passengerPhone?: string
    paymentResponsibility?: 'passenger' | 'admin' | 'account'
    paymentSourceAccount?: string
    settlementStatus?: 'pending' | 'cleared'
    invoiceGroup?: string
    internalNote?: string
  }) {
    const response = await apiClient.post('/api/cms/bookings', bookingData)
    return response.data
  }

  async updateBooking(bookingId: string, bookingData: {
    seatNumbers?: number[]
    boardingStopId?: string
    alightingStopId?: string
    fareAmount?: number
    paymentStatus?: 'pending' | 'paid' | 'failed' | 'refunded'
    passengerName?: string
    passengerPhone?: string
    paymentResponsibility?: 'passenger' | 'admin' | 'account'
    paymentSourceAccount?: string
    settlementStatus?: 'pending' | 'cleared'
    settlementClearedAt?: string
    settlementClearedById?: string
    invoiceGroup?: string
    internalNote?: string
  }) {
    const response = await apiClient.put(`/api/cms/bookings/${bookingId}`, bookingData)
    return response.data
  }

  async deleteBooking(bookingId: string) {
    const response = await apiClient.delete(`/api/cms/bookings/${bookingId}`)
    return response.data
  }

  async getBookingPassengers(bookingId: string) {
    const response = await apiClient.get(`/api/cms/bookings/${bookingId}/passengers`)
    return response.data
  }

  async getBookingPayment(bookingId: string) {
    const response = await apiClient.get(`/api/cms/bookings/${bookingId}/payment`)
    return response.data
  }

  async processRefund(bookingId: string, refundData: {
    reason?: string
    refundAmount?: number
  }) {
    const response = await apiClient.patch(`/api/cms/bookings/${bookingId}/refund`, refundData)
    return response.data
  }

  async manageSeats(bookingId: string, seatData: {
    action: 'add' | 'remove' | 'replace'
    seatNumbers: number[]
  }) {
    const response = await apiClient.post(`/api/cms/bookings/${bookingId}/seats`, seatData)
    return response.data
  }

  async bulkCreateBookings(bookings: Array<{
    userId: string
    tripId: string
    seatNumbers: number[]
    boardingStopId?: string
    alightingStopId?: string
    fareAmount?: number
  }>) {
    const response = await apiClient.post('/api/cms/bookings/bulk', { bookings })
    return response.data
  }

  async bulkUpdateBookings(updates: Array<{
    id: string
    seatNumbers?: number[]
    fareAmount?: number
    bookingStatus?: string
    boardingStopId?: string
    alightingStopId?: string
  }>) {
    const response = await apiClient.put('/api/cms/bookings/bulk', { updates })
    return response.data
  }

  async bulkDeleteBookings(bookingIds: string[]) {
    const response = await apiClient.delete('/api/cms/bookings/bulk', { data: { bookingIds } })
    return response.data
  }

  // Passengers / Commuters Management
  async getPassengers(params?: PaginationParams) {
    const response = await apiClient.get('/api/cms/passengers', { params })
    return response.data
  }

  async getPassengerStats() {
    const response = await apiClient.get('/api/cms/passengers/statistics')
    return response.data
  }

  async getPassengerById(passengerId: string) {
    const response = await apiClient.get(`/api/passengers/${passengerId}`)
    return response.data
  }

  async updatePassenger(passengerId: string, data: { firstName?: string; lastName?: string; email?: string; phoneNumber?: string }) {
    const response = await apiClient.patch(`/api/cms/passengers/${passengerId}`, data)
    return response.data
  }

  async updatePassengerStatus(passengerId: string, status: 'active' | 'inactive' | 'suspended') {
    const response = await apiClient.patch(`/api/cms/passengers/${passengerId}/status`, { status })
    return response.data
  }

  async cleanupPendingKycPassengers(data: { userIds: string[]; forceDelete?: boolean; minimumAgeHours?: number }) {
    const response = await apiClient.post('/api/cms/passengers/cleanup-pending-kyc', data)
    return response.data
  }

  async getPendingKycCleanupEmailAttempts(status: 'failed' | 'sent' = 'failed') {
    const response = await apiClient.get('/api/cms/passengers/cleanup-pending-kyc/emails', {
      params: { status },
    })
    return response.data
  }

  async resendPendingKycCleanupEmails(data: { notificationIds: string[]; emailSubject: string; emailMessage: string }) {
    const response = await apiClient.post('/api/cms/passengers/cleanup-pending-kyc/emails/resend', data)
    return response.data
  }

  async sendEmailToUser(userId: string, data: { subject: string; message: string; email: string }) {
    const response = await apiClient.post('/api/notifications/send', {
      userId,
      type: 'email',
      recipient: data.email,
      title: data.subject,
      message: data.message,
      data: { title: data.subject, message: data.message },
      priority: 'normal',
    })
    return response.data
  }

  // Trip Management
  async getTrips(params?: PaginationParams & {
    status?: string
    daladalaId?: string
    routeId?: string
    date?: string
  }) {
    const response = await apiClient.get('/api/cms/trips', { params })
    return response.data
  }

  async getTripById(tripId: string) {
    const response = await apiClient.get(`/api/cms/trips/${tripId}`)
    return response.data
  }

  async createTrip(tripData: {
    daladalaId: string
    routeId: string
    timetableId?: string
    scheduledDeparture: string
    scheduledArrival: string
  }) {
    const response = await apiClient.post('/api/cms/trips', {
      ...tripData,
      scheduledDeparture: toIsoDateTime(tripData.scheduledDeparture),
      scheduledArrival: toIsoDateTime(tripData.scheduledArrival),
    })
    return response.data
  }

  async updateTripStatus(tripId: string, status: string) {
    const response = await apiClient.patch(`/api/cms/trips/${tripId}/status`, { status })
    return response.data
  }

  async getActiveTrips() {
    const response = await apiClient.get('/api/cms/trips/active')
    return response.data
  }

  async getTripsForDate(date: string) {
    const response = await apiClient.get('/api/cms/trips/date', {
      params: { date }
    })
    return response.data
  }

  async getTripSeatMap(tripId: string, params?: { fromStopId?: string; toStopId?: string }) {
    const response = await apiClient.get(`/booking/availability/trip/${tripId}/seats`, { params })
    return response.data
  }

  // Timetable Management
  async getTimetables(params?: PaginationParams & {
    daladalaId?: string
    routeId?: string
    dayOfWeek?: number
  }) {
    const response = await apiClient.get('/api/cms/timetables', { params })
    return response.data
  }

  async getTimetableById(timetableId: string) {
    const response = await apiClient.get(`/api/cms/timetables/${timetableId}`)
    return response.data
  }

  async updateTimetable(timetableId: string, data: {
    dayOfWeek?: number
    departureTime?: string
    arrivalTime?: string
    active?: boolean
  }) {
    const response = await apiClient.patch(`/api/cms/timetables/${timetableId}`, data)
    return response.data
  }

  async getTimetableByRoute(routeId: string) {
    const response = await apiClient.get(`/api/cms/timetables/route/${routeId}`)
    return response.data
  }

  async getTimetableByDaladala(daladalaId: string) {
    const response = await apiClient.get(`/api/cms/timetables/daladala/${daladalaId}`)
    return response.data
  }

  // Analytics
  async getAnalytics(params?: {
    period?: string
    startDate?: string
    endDate?: string
  }) {
    // const response = await apiClient.get('/analytics/dashboard', { params })
    const response = await apiClient.get('/api/cms/analytics/dashboard', { params })
    return response.data
  }

  // Settings API
  async getSettings() {
    const response = await apiClient.get('/api/cms/settings')
    return response.data
  }

  async getOwnerDashboardSettings() {
    const response = await apiClient.get('/api/cms/settings/owner-dashboard')
    return response.data
  }

  async getReferralSettings() {
    const response = await apiClient.get('/api/cms/settings/referral')
    return response.data
  }

  async getFareCalculatorSettings() {
    const response = await apiClient.get('/api/cms/settings/fare-calculator')
    return response.data
  }

  async getLatraTariffs() {
    const response = await apiClient.get('/api/cms/settings/latra-tariffs')
    return response.data
  }

  async updateLatraTariffs(data: any) {
    const response = await apiClient.put('/api/cms/settings/latra-tariffs', data)
    return response.data
  }

  async getBookingCancellationSettings() {
    const response = await apiClient.get('/api/cms/settings/booking-cancellation', {
      validateStatus: (status) => (status >= 200 && status < 300) || status === 404,
    })
    if (response.status === 404) {
      return defaultBookingCancellationSettingsResponse()
    }
    return response.data
  }

  async updateGeneralSettings(data: any) {
    const response = await apiClient.put('/api/cms/settings/general', data)
    return response.data
  }

  async updateFareCalculatorSettings(data: any) {
    const response = await apiClient.put('/api/cms/settings/fare-calculator', data)
    return response.data
  }

  async updateReferralSettings(data: any) {
    const response = await apiClient.put('/api/cms/settings/referral', data)
    return response.data
  }

  async updateSMSSettings(data: any) {
    const response = await apiClient.put('/api/cms/settings/sms', data)
    return response.data
  }

  async updateEmailSettings(data: any) {
    const response = await apiClient.put('/api/cms/settings/email', data)
    return response.data
  }

  async updatePaymentSettings(data: any) {
    const response = await apiClient.put('/api/cms/settings/payment', data)
    return response.data
  }

  async updateBookingCancellationSettings(data: any) {
    const response = await apiClient.put('/api/cms/settings/booking-cancellation', data)
    return response.data
  }

  async updateOwnerDashboardSettings(data: any) {
    const response = await apiClient.put('/api/cms/settings/owner-dashboard', data)
    return response.data
  }

  async toggleMaintenanceMode() {
    const response = await apiClient.post('/api/cms/settings/maintenance-mode')
    return response.data
  }

  async clearCache() {
    const response = await apiClient.post('/api/cms/settings/clear-cache')
    return response.data
  }

  async createBackup() {
    const response = await apiClient.post('/api/cms/settings/backup')
    return response.data
  }

  // Payment Method Prefix Management
  async getPaymentPrefixes() {
    const response = await apiClient.get('/api/cms/settings/payment-prefixes')
    return response.data
  }

  async createPaymentPrefix(data: { prefix: string; paymentMethod: string; operator: string }) {
    const response = await apiClient.post('/api/cms/settings/payment-prefixes', data)
    return response.data
  }

  async updatePaymentPrefix(id: string, data: { prefix?: string; paymentMethod?: string; operator?: string; active?: boolean }) {
    const response = await apiClient.patch(`/api/cms/settings/payment-prefixes/${id}`, data)
    return response.data
  }

  async deletePaymentPrefix(id: string) {
    const response = await apiClient.delete(`/api/cms/settings/payment-prefixes/${id}`)
    return response.data
  }

  // Deletion API
  async requestAccountDeletion(data: {
    email: string
    phoneNumber: string
    password: string
    reason?: string
  }) {
    const response = await apiClient.post('/user-management/deletion-requests/account', data)
    return response.data
  }

  async requestDataDeletion(data: {
    email: string
    phoneNumber: string
    password: string
    dataTypes: string[]
    reason?: string
  }) {
    const response = await apiClient.post('/user-management/deletion-requests/data', data)
    return response.data
  }

  async getDeletionRequests() {
    const response = await apiClient.get('/user-management/deletion-requests/my-requests')
    return response.data
  }

  async cancelDeletionRequest(requestId: string) {
    const response = await apiClient.post(`/user-management/deletion-requests/${requestId}/cancel`)
    return response.data
  }

  // ── Bus Owner (Operator) Management ──────────────────────────────────────

  async createOperator(data: {
    firstName: string
    lastName: string
    email: string
    phoneNumber?: string
    password: string
  }) {
    const response = await apiClient.post('/api/cms/admin/operators', data)
    return response.data
  }

  async updateOperatorRole(userId: string, role: string) {
    const response = await apiClient.patch(`/api/cms/admin/users/${userId}/role`, { role })
    return response.data
  }

  async deactivateOperator(userId: string) {
    const response = await apiClient.delete(`/api/cms/admin/users/${userId}`)
    return response.data
  }

  // ── Notifications ──────────────────────────────────────────────────────────

  async sendNotification(data: {
    userId?: string
    type: 'push' | 'sms' | 'email'
    recipient?: string
    template?: string
    title?: string
    message?: string
    data?: any
    priority?: 'low' | 'normal' | 'high'
  }) {
    // If no template is provided, we might be sending a custom message
    // The notification service might expect 'title' and 'message' in 'data' if no template
    const payload = {
      ...data,
      data: {
        title: data.title,
        message: data.message,
        ...data.data,
      },
    }
    const response = await apiClient.post('/api/notifications/send', payload)
    return response.data
  }

  async getNotificationTemplates(type?: 'push' | 'sms' | 'email') {
    const response = await apiClient.get('/api/notifications/templates', {
      params: { type },
    })
    return response.data
  }

  async getNotificationSegments() {
    const response = await apiClient.get('/api/notifications/segments')
    return response.data
  }

  async sendToSegment(data: {
    segmentId: string
    template?: string
    title?: string
    message?: string
    data?: any
    channels: ('push' | 'sms' | 'email')[]
    priority?: 'low' | 'normal' | 'high'
  }) {
    const payload = {
      ...data,
      data: {
        title: data.title,
        message: data.message,
        ...data.data,
      },
    }
    const response = await apiClient.post('/api/notifications/send-to-segment', payload)
    return response.data
  }

  async getNotificationHistory(params?: PaginationParams) {
    const response = await apiClient.get('/api/notifications/history', { params })
    return response.data
  }

  // ── Push Notifications (Admin Broadcast) ─────────────────────────────────

  async sendBroadcastNotification(data: { title: string; body: string; targetRole?: string }) {
    const response = await apiClient.post('/api/cms/notifications/broadcast', data)
    return response.data
  }

  async sendDirectNotification(data: { userIds: string[]; title: string; body: string; data?: any }) {
    const response = await apiClient.post('/api/cms/notifications/direct', data)
    return response.data
  }

  // LATRA Test Unit
  async getLatraOverview() {
    const response = await apiClient.get('/api/cms/latra/overview')
    return response.data
  }

  async getLatraVehicleCandidates() {
    const response = await apiClient.get('/api/cms/latra/vehicles/candidates')
    return response.data
  }

  async getLatraDriverCandidates() {
    const response = await apiClient.get('/api/cms/latra/drivers/candidates')
    return response.data
  }

  async getLatraTicketCandidates() {
    const response = await apiClient.get('/api/cms/latra/tickets/candidates')
    return response.data
  }

  async getLatraDailyReportPreview(reportDate: string) {
    const response = await apiClient.get('/api/cms/latra/daily-report/preview', {
      params: { reportDate },
    })
    return response.data
  }

  async getLatraDailyReportCandidates(days = 3650) {
    const response = await apiClient.get('/api/cms/latra/daily-report/candidates', {
      params: { days },
    })
    return response.data
  }

  async getLatraJwksHealth() {
    const response = await apiClient.get('/api/cms/latra/jwks-health')
    return response.data
  }

  async probeLatraAuth() {
    const response = await apiClient.post('/api/cms/latra/auth/probe')
    return response.data
  }

  async buildLatraVehiclePayload(vehicleId: string) {
    const response = await apiClient.post('/api/cms/latra/vehicles/build', { vehicleId })
    return response.data
  }

  async submitLatraVehicle(vehicleId: string) {
    const response = await apiClient.post('/api/cms/latra/vehicles/submit', { vehicleId })
    return response.data
  }

  async submitLatraVehiclesBulk(vehicleIds: string[]) {
    const response = await apiClient.post('/api/cms/latra/vehicles/submit-bulk', { vehicleIds })
    return response.data
  }

  async buildLatraDriverPayload(driverId: string) {
    const response = await apiClient.post('/api/cms/latra/drivers/build', { driverId })
    return response.data
  }

  async submitLatraDriver(driverId: string) {
    const response = await apiClient.post('/api/cms/latra/drivers/submit', { driverId })
    return response.data
  }

  async submitLatraDriversBulk(driverIds: string[]) {
    const response = await apiClient.post('/api/cms/latra/drivers/submit-bulk', { driverIds })
    return response.data
  }

  async buildLatraTicketPayload(bookingId: string) {
    const response = await apiClient.post('/api/cms/latra/tickets/build', { bookingId })
    return response.data
  }

  async submitLatraTicket(bookingId: string) {
    const response = await apiClient.post('/api/cms/latra/tickets/submit', { bookingId })
    return response.data
  }

  async buildLatraDailyReport(reportDate: string) {
    const response = await apiClient.post('/api/cms/latra/daily-report/build', { reportDate })
    return response.data
  }

  async submitLatraDailyReport(reportDate: string) {
    const response = await apiClient.post('/api/cms/latra/daily-report/submit', { reportDate })
    return response.data
  }

  async submitLatraDailyReportsBulk(reportDates: string[]) {
    const response = await apiClient.post('/api/cms/latra/daily-report/submit-bulk', { reportDates })
    return response.data
  }

  async getLatraControl() {
    const response = await apiClient.get('/api/cms/latra/control')
    return response.data
  }

  async updateLatraControl(payload: Record<string, unknown>) {
    const response = await apiClient.patch('/api/cms/latra/control', payload)
    return response.data
  }

  async getLatraSubmissions(params?: Record<string, string | number>) {
    const response = await apiClient.get('/api/cms/latra/submissions', { params })
    return response.data
  }

  async getLatraSubmission(id: string) {
    const response = await apiClient.get(`/api/cms/latra/submissions/${id}`)
    return response.data
  }

  async getLatraSubmissionAnalytics(days = 14) {
    const response = await apiClient.get('/api/cms/latra/submissions/analytics', {
      params: { days },
    })
    return response.data
  }


  async runLatraPassthroughOnce() {
    const response = await apiClient.post('/api/cms/latra/passthrough/run-once')
    return response.data
  }


  // ── Driver Applications & Hiring ──────────────────────────────────────────

  async getApplications(params?: ApplicationFilters) {
    const response = await apiClient.get('/user-management/hiring/applications', { params })
    return response.data
  }

  async getApplicationById(id: string) {
    const response = await apiClient.get(`/user-management/hiring/applications/${id}`)
    return response.data
  }

  async reviewApplication(id: string, data: { status: 'UNDER_REVIEW' | 'APPROVED' | 'REJECTED'; reviewNotes?: string; rejectionReason?: string }) {
    const response = await apiClient.patch(`/user-management/hiring/applications/${id}/review`, data)
    return response.data
  }

  async hireApplicant(id: string, data: { hiredRole: 'DRIVER' | 'CONDUCTOR'; assignedBusId: string }) {
    const response = await apiClient.post(`/user-management/hiring/applications/${id}/hire`, data)
    return response.data
  }

  async getJobPostings() {
    // We get them from the hiring routes (admin view)
    const response = await apiClient.get('/user-management/hiring/jobs')
    // Map backend schema to frontend schema
    if (response.data?.data) {
      response.data.data = response.data.data.map((job: any) => ({
        ...job,
        role: job.roleType,
        status: job.isActive ? 'ACTIVE' : 'CLOSED',
        requirements: job.requirements || [],
        salaryRange: job.salaryRange || '',
        location: job.location || ''
      }))
    }
    return response.data
  }

  async createJobPosting(data: {
    title: string
    description: string
    role: 'DRIVER' | 'CONDUCTOR'
    requirements?: string[]
    salaryRange?: string
    location?: string
  }) {
    const payload = {
      title: data.title,
      description: data.description,
      roleType: data.role,
      isActive: true,
      requirements: data.requirements || [],
      salaryRange: data.salaryRange || '',
      location: data.location || ''
    }
    const response = await apiClient.post('/user-management/hiring/jobs', payload)
    return response.data
  }

  async updateJobPosting(id: string, data: {
    title?: string
    description?: string
    role?: 'DRIVER' | 'CONDUCTOR'
    requirements?: string[]
    salaryRange?: string
    location?: string
    status?: 'ACTIVE' | 'CLOSED' | 'DRAFT'
  }) {
    const payload: any = { ...data }
    if (data.role) payload.roleType = data.role
    if (data.status) payload.isActive = data.status === 'ACTIVE'
    
    delete payload.role
    delete payload.status

    const response = await apiClient.patch(`/user-management/hiring/jobs/${id}`, payload)
    return response.data
  }
}

// Export singleton instance
export const cmsService = new CMSService()

// Export axios instance for custom requests
export { apiClient }
